import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Title2,
  Body1,
  Subtitle1,
  Subtitle2,
  Spinner,
  Button,
  Input,
  Badge,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  Field,
  OverlayDrawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody,
  Toaster,
  useToastController,
  useId,
  Toast,
  ToastTitle,
  ToastBody,
  makeStyles,
  tokens
} from "@fluentui/react-components";
import { fetchWithRetry } from "../lib/fetchWithRetry";
import BalancePanel from "../components/BalancePanel";

interface TeamMember {
  id: string;
  displayName: string;
  email: string;
  departmentId: string | null;
  departmentName: string | null;
  jobTitle: string | null;
  managerId: string | null;
  managerName: string | null;
}

interface MyTeamResponse {
  scope: "admin" | "manager" | "none";
  users: TeamMember[];
}

interface RequestRow {
  id: string;
  requestNumber: number;
  leaveTypeName: string;
  status: "pending" | "approved" | "denied" | "cancelled";
  startDate: string;
  endDate: string;
  workDays: number;
  halfDay: boolean;
  amPm: "AM" | "PM" | null;
  approvedByName: string | null;
  denialReason: string | null;
  submittedAt: string | null;
}

interface RequestsResponse {
  userId: string;
  userDisplayName: string;
  requests: RequestRow[];
}

interface BalanceRow {
  leaveTypeId: string;
  leaveTypeName: string;
  sortOrder: number;
  accrued: number;
  used: number;
  pending: number;
  remaining: number;
}

interface BalancesResponse {
  userId: string;
  userDisplayName: string;
  fiscalYear: number;
  balances: BalanceRow[];
}

const useStyles = makeStyles({
  headerRow: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalL,
    marginBottom: tokens.spacingVerticalS
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalM,
    flexWrap: "wrap"
  },
  search: {
    minWidth: "280px"
  },
  empty: {
    padding: tokens.spacingVerticalXL,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium
  },
  error: { color: tokens.colorPaletteRedForeground1 },
  drawerSubtitle: {
    color: tokens.colorNeutralForeground3,
    marginBottom: tokens.spacingVerticalM
  },
  section: {
    marginTop: tokens.spacingVerticalXL,
    paddingTop: tokens.spacingVerticalM,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`
  },
  adjustList: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    marginTop: tokens.spacingVerticalM
  },
  adjustRow: {
    display: "flex",
    alignItems: "flex-end",
    gap: tokens.spacingHorizontalM,
    flexWrap: "wrap"
  },
  adjustLabel: {
    flex: "1 1 160px",
    minWidth: "160px",
    color: tokens.colorNeutralForeground2
  },
  adjustInput: {
    width: "100px"
  },
  adjustNote: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200
  }
});

function statusBadge(s: RequestRow["status"]) {
  if (s === "approved") return <Badge appearance="filled" color="success">approved</Badge>;
  if (s === "pending") return <Badge appearance="filled" color="warning">pending</Badge>;
  if (s === "denied") return <Badge appearance="outline" color="danger">denied</Badge>;
  return <Badge appearance="outline" color="subtle">cancelled</Badge>;
}

function formatDateRange(startIso: string, endIso: string): string {
  const fmt = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
  if (startIso === endIso) return fmt.format(new Date(startIso));
  return `${fmt.format(new Date(startIso))} – ${fmt.format(new Date(endIso))}`;
}

function formatDays(r: RequestRow): string {
  if (r.halfDay) return `½${r.amPm ? ` ${r.amPm}` : ""}`;
  return r.workDays === 1 ? "1 day" : `${r.workDays} days`;
}

export default function TeamBalances() {
  const styles = useStyles();
  const toasterId = useId("team-toaster");
  const { dispatchToast } = useToastController(toasterId);

  const [data, setData] = useState<MyTeamResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [warming, setWarming] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<TeamMember | null>(null);

  const load = useCallback(() => {
    setError(null);
    return fetchWithRetry("/api/my-team", undefined, { onWarming: () => setWarming(true) })
      .then(async (r) => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(body.error ?? `HTTP ${r.status}`);
        }
        return (await r.json()) as MyTeamResponse;
      })
      .then((d) => { setData(d); setWarming(false); })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  useEffect(() => {
    setData(null);
    setWarming(false);
    load();
  }, [load]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    if (!q) return data.users;
    return data.users.filter(
      (u) =>
        u.displayName.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        (u.departmentName ?? "").toLowerCase().includes(q) ||
        (u.jobTitle ?? "").toLowerCase().includes(q)
    );
  }, [data, query]);

  if (error) {
    return (
      <div>
        <Toaster toasterId={toasterId} />
        <Title2 as="h2" block>Team</Title2>
        <Body1 as="p" className={styles.error}>Error loading team: {error}</Body1>
      </div>
    );
  }

  if (!data) {
    return (
      <div>
        <Toaster toasterId={toasterId} />
        <Title2 as="h2" block>Team</Title2>
        <Spinner
          size="tiny"
          label={warming ? "Waking up the database… 30–60s the first time after hours." : "Loading…"}
        />
      </div>
    );
  }

  if (data.scope === "none") {
    return (
      <div>
        <Toaster toasterId={toasterId} />
        <Title2 as="h2" block>Team</Title2>
        <div className={styles.empty}>
          <Body1>You don't manage anyone, and you aren't an administrator.</Body1>
        </div>
      </div>
    );
  }

  const isAdminScope = data.scope === "admin";
  const scopeLabel = isAdminScope
    ? `everyone (${data.users.length} active users)`
    : `your direct reports (${data.users.length})`;

  return (
    <div>
      <Toaster toasterId={toasterId} />
      <div className={styles.headerRow}>
        <div>
          <Title2 as="h2" block>Team</Title2>
          <Body1 as="p">Click a row to view balances and recent requests. Showing {scopeLabel}.</Body1>
        </div>
        <Badge appearance="outline" color={isAdminScope ? "brand" : "informative"}>
          {isAdminScope ? "Admin scope" : "Manager scope"}
        </Badge>
      </div>

      <div className={styles.toolbar}>
        <Input
          className={styles.search}
          placeholder="Search by name, email, department, or job title"
          value={query}
          onChange={(_, d) => setQuery(d.value)}
        />
      </div>

      {filtered.length === 0 ? (
        <div className={styles.empty}>
          <Body1>{data.users.length === 0 ? "No team members to show." : "No matches for that search."}</Body1>
        </div>
      ) : (
        <Table aria-label="Team members">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Name</TableHeaderCell>
              <TableHeaderCell>Department</TableHeaderCell>
              <TableHeaderCell>Job title</TableHeaderCell>
              {isAdminScope && <TableHeaderCell>Manager</TableHeaderCell>}
              <TableHeaderCell aria-label="Actions" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((u) => (
              <TableRow key={u.id}>
                <TableCell>
                  <div>{u.displayName}</div>
                  <Body1 style={{ color: tokens.colorNeutralForeground3 }}>{u.email}</Body1>
                </TableCell>
                <TableCell>{u.departmentName ?? "—"}</TableCell>
                <TableCell>{u.jobTitle ?? "—"}</TableCell>
                {isAdminScope && <TableCell>{u.managerName ?? "—"}</TableCell>}
                <TableCell style={{ whiteSpace: "nowrap" }}>
                  <Button size="small" appearance="primary" onClick={() => setSelected(u)}>
                    View details
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <TeamMemberDrawer
        user={selected}
        isAdmin={isAdminScope}
        onClose={() => setSelected(null)}
        onAdjusted={(typeName) =>
          dispatchToast(
            <Toast><ToastTitle>Allocation saved</ToastTitle><ToastBody>{typeName} accrual updated.</ToastBody></Toast>,
            { intent: "success" }
          )
        }
      />
    </div>
  );
}

interface DrawerProps {
  user: TeamMember | null;
  isAdmin: boolean;
  onClose: () => void;
  onAdjusted: (leaveTypeName: string) => void;
}

function TeamMemberDrawer({ user, isAdmin, onClose, onAdjusted }: DrawerProps) {
  const styles = useStyles();
  const [requests, setRequests] = useState<RequestRow[] | null>(null);
  const [requestsError, setRequestsError] = useState<string | null>(null);
  const [balances, setBalances] = useState<BalancesResponse | null>(null);
  const [balancesError, setBalancesError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  const loadDrawer = useCallback((u: TeamMember) => {
    setRequests(null);
    setRequestsError(null);
    setBalances(null);
    setBalancesError(null);

    // Scope to the current calendar year — matches how balances are scoped
    // and matches what a manager actually wants to see when reviewing.
    const yearStart = `${new Date().getUTCFullYear()}-01-01`;
    fetchWithRetry(
      `/api/leave-requests?userId=${encodeURIComponent(u.id)}&from=${yearStart}&limit=50`
    )
      .then(async (r) => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(body.error ?? `HTTP ${r.status}`);
        }
        return (await r.json()) as RequestsResponse;
      })
      .then((d) => setRequests(d.requests))
      .catch((e) => setRequestsError(e instanceof Error ? e.message : String(e)));

    // Balances fetched only for the admin adjust panel — the read-only
    // BalancePanel below handles its own fetch via the userIdParam prop.
    if (isAdmin) {
      fetchWithRetry(`/api/leave-balances?userId=${encodeURIComponent(u.id)}`)
        .then(async (r) => {
          if (!r.ok) {
            const body = await r.json().catch(() => ({}));
            throw new Error(body.error ?? `HTTP ${r.status}`);
          }
          return (await r.json()) as BalancesResponse;
        })
        .then((d) => setBalances(d))
        .catch((e) => setBalancesError(e instanceof Error ? e.message : String(e)));
    }
  }, [isAdmin]);

  useEffect(() => {
    if (user) loadDrawer(user);
  }, [user, loadDrawer]);

  async function saveAccrued(typeId: string, typeName: string, accrued: number) {
    if (!user) return;
    const r = await fetch(
      `/api/manage-users/${encodeURIComponent(user.id)}/balances/${encodeURIComponent(typeId)}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accrued })
      }
    );
    if (!r.ok) {
      const body = await r.json().catch(() => ({}));
      throw new Error(body.error ?? `HTTP ${r.status}`);
    }
    // Bump version so the read-only BalancePanel refetches; also reload our
    // own balances so the next "before" value in the admin panel is right.
    setVersion((v) => v + 1);
    loadDrawer(user);
    onAdjusted(typeName);
  }

  return (
    <OverlayDrawer
      open={user !== null}
      onOpenChange={(_, d) => !d.open && onClose()}
      position="end"
      size="large"
    >
      <DrawerHeader>
        <DrawerHeaderTitle>{user?.displayName ?? "Details"}</DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>
        {user && (
          <>
            <Subtitle1 as="h3" block>{user.displayName}</Subtitle1>
            <Body1 className={styles.drawerSubtitle}>
              {user.email}
              {user.departmentName ? ` · ${user.departmentName}` : ""}
              {user.jobTitle ? ` · ${user.jobTitle}` : ""}
            </Body1>

            <BalancePanel userIdParam={user.id} version={version} />

            {isAdmin && (
              <AdminAdjustSection
                balances={balances}
                error={balancesError}
                onSave={saveAccrued}
              />
            )}

            <div className={styles.section}>
              <Subtitle2 as="h4" block>Requests this year</Subtitle2>
              {requestsError && (
                <Body1 as="p" className={styles.error}>Couldn't load requests: {requestsError}</Body1>
              )}
              {!requests && !requestsError && <Spinner size="tiny" label="Loading…" />}
              {requests && requests.length === 0 && (
                <Body1 as="p" style={{ color: tokens.colorNeutralForeground3 }}>
                  No requests so far this year.
                </Body1>
              )}
              {requests && requests.length > 0 && (
                <Table aria-label="Recent requests" size="small">
                  <TableHeader>
                    <TableRow>
                      <TableHeaderCell>#</TableHeaderCell>
                      <TableHeaderCell>Type</TableHeaderCell>
                      <TableHeaderCell>Dates</TableHeaderCell>
                      <TableHeaderCell>Days</TableHeaderCell>
                      <TableHeaderCell>Status</TableHeaderCell>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {requests.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell>{r.requestNumber}</TableCell>
                        <TableCell>{r.leaveTypeName}</TableCell>
                        <TableCell>{formatDateRange(r.startDate, r.endDate)}</TableCell>
                        <TableCell>{formatDays(r)}</TableCell>
                        <TableCell>{statusBadge(r.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </>
        )}
      </DrawerBody>
    </OverlayDrawer>
  );
}

interface AdminAdjustSectionProps {
  balances: BalancesResponse | null;
  error: string | null;
  onSave: (typeId: string, typeName: string, accrued: number) => Promise<void>;
}

function AdminAdjustSection({ balances, error, onSave }: AdminAdjustSectionProps) {
  const styles = useStyles();
  return (
    <div className={styles.section}>
      <Subtitle2 as="h4" block>Adjust allocations</Subtitle2>
      <Body1 className={styles.adjustNote}>
        Sets the <strong>Accrued</strong> value for the current fiscal year. Used/Pending stay computed
        from approved + pending requests; Remaining auto-updates.
      </Body1>
      {error && <Body1 as="p" className={styles.error}>Couldn't load balances: {error}</Body1>}
      {!balances && !error && <Spinner size="tiny" label="Loading…" />}
      {balances && balances.balances.length === 0 && (
        <Body1 style={{ color: tokens.colorNeutralForeground3 }}>No balance-tracked leave types.</Body1>
      )}
      {balances && balances.balances.length > 0 && (
        <div className={styles.adjustList}>
          {balances.balances.map((b) => (
            <AdjustRow key={b.leaveTypeId} balance={b} onSave={onSave} />
          ))}
        </div>
      )}
    </div>
  );
}

interface AdjustRowProps {
  balance: BalanceRow;
  onSave: (typeId: string, typeName: string, accrued: number) => Promise<void>;
}

function AdjustRow({ balance, onSave }: AdjustRowProps) {
  const styles = useStyles();
  const [value, setValue] = useState<string>(String(balance.accrued));
  const [submitting, setSubmitting] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);

  useEffect(() => { setValue(String(balance.accrued)); setRowError(null); }, [balance.accrued]);

  const parsed = Number(value);
  const dirty = !Number.isNaN(parsed) && parsed !== balance.accrued;
  const canSave = dirty && parsed >= 0 && !submitting;

  async function handleSave() {
    setSubmitting(true);
    setRowError(null);
    try {
      await onSave(balance.leaveTypeId, balance.leaveTypeName, Math.round(parsed * 10) / 10);
    } catch (e) {
      setRowError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={styles.adjustRow}>
      <div className={styles.adjustLabel}>
        <Body1>{balance.leaveTypeName}</Body1>
        <Body1 className={styles.adjustNote}>
          Used {balance.used} · Pending {balance.pending} · Remaining {balance.remaining}
        </Body1>
      </div>
      <Field label="Accrued" hint={rowError ?? undefined} validationState={rowError ? "error" : undefined}>
        <Input
          className={styles.adjustInput}
          type="number"
          inputMode="decimal"
          step="0.5"
          min="0"
          value={value}
          onChange={(_, d) => setValue(d.value)}
        />
      </Field>
      <Button appearance="primary" size="small" disabled={!canSave} onClick={handleSave}>
        {submitting ? "Saving…" : "Save"}
      </Button>
    </div>
  );
}
